import { useEffect, useState } from "react";

/**
 * Custom hook for managing a timer.
 *
 * @returns {Object} An object containing the following properties and methods:
 *   - timeLeft: The remaining time in seconds.
 *   - isRunning: A boolean indicating whether the timer is currently running.
 *   - start: A function to start the timer.
 *   - pause: A function to pause the timer.
 *   - reset: A function to reset the timer.
 */
export default function useTimer() {
  const [timeLeft, setTimeLeft] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [intervalId, setIntervalId] = useState(null);

  useEffect(() => {
    if (timeLeft === 0) {
      clearInterval(intervalId);
      setIsRunning(false);
    }
  }, [timeLeft]);

  useEffect(() => {
    return () => {
      clearInterval(intervalId);
    };
  }, []);

  const start = (seconds) => {
    setTimeLeft(seconds);
    setIsRunning(true);
    setIntervalId(
      setInterval(() => {
        setTimeLeft((prevTimeLeft) => prevTimeLeft - 1);
      }, 1000)
    );
  };

  const pause = () => {
    clearInterval(intervalId);
    setIsRunning(false);
  };

  const reset = () => {
    clearInterval(intervalId);
    setIsRunning(false);
    setTimeLeft(0);
  };

  return { timeLeft, isRunning, start, pause, reset };
}
